import type { ApiClientError } from 'api/client';
import HeapUtils from 'app/utils/heap';
import PageTypeUtils from 'app/utils/pageType';
import { isMetaSafeConnectEligible } from 'app/modules/socialLogin/utils';
import { SocialLoginTokenType } from 'types/SocialLogin';
import { BrowserStorage } from 'utils/browserStorage';
import Logger from 'utils/logger';

export type ConnectSource = 'onboarding' | 'add_profile';

export type ConnectMethod = 'business_portfolio' | 'admin_access' | 'standard';

export type ConnectOauthResult = 'success' | 'error' | 'cancel';

export interface ConnectAttempt {
  network: string;
  source?: ConnectSource;
  method?: ConnectMethod;
  attemptNumber?: number;
  startedAt?: number;
  hasReturned?: boolean;
  failureReason?: string;
}

interface SelectNetworkParams {
  accountId: number | null;
  pageType: number;
  source: ConnectSource;
}

const ATTEMPT_KEY = BrowserStorage.keys.ConnectTracking.Attempt;
const ATTEMPT_COUNTS_KEY = BrowserStorage.keys.ConnectTracking.AttemptCounts;

const read = <T>(key: string): T | null => {
  try {
    const value = sessionStorage.getItem(key);

    return value ? (JSON.parse(value) as T) : null;
  } catch (error) {
    Logger.error(error);
    return null;
  }
};

const write = (key: string, value: unknown) => {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
  } catch (error) {
    Logger.error(error);
  }
};

const remove = (key: string) => {
  try {
    sessionStorage.removeItem(key);
  } catch (error) {
    Logger.error(error);
  }
};

export const getConnectNetwork = (pageType: number | null | undefined) =>
  pageType != null ? PageTypeUtils.getName(pageType) || null : null;

export const getConnectMethod = (
  pageType: number,
  tokenType?: SocialLoginTokenType,
): ConnectMethod | undefined => {
  if (!isMetaSafeConnectEligible(pageType)) {
    return 'standard';
  }

  if (tokenType === 'user') {
    return 'admin_access';
  }

  return tokenType ? 'business_portfolio' : undefined;
};

export const getConnectErrorCode = (
  error?: ApiClientError | null,
): string | null => {
  const code =
    error?.response?.data?.outcome ??
    error?.response?.status ??
    error?.axiosError?.response?.status;

  return code != null ? String(code) : null;
};

export const getConnectDurationSec = (
  attempt: ConnectAttempt | null,
  now = Date.now(),
): number | null =>
  attempt?.startedAt
    ? Math.max(0, Math.round((now - attempt.startedAt) / 1000))
    : null;

export const connectTracking = {
  get: () => read<ConnectAttempt>(ATTEMPT_KEY),
  selectNetwork({ accountId, pageType, source }: SelectNetworkParams): number {
    const network = getConnectNetwork(pageType) ?? String(pageType);
    const counts = read<Record<string, number>>(ATTEMPT_COUNTS_KEY) ?? {};
    const countKey = `${accountId}:${network}`;
    const attemptNumber = (counts[countKey] ?? 0) + 1;

    const attempt: ConnectAttempt = {
      network,
      source,
      attemptNumber,
      method: getConnectMethod(pageType),
    };

    write(ATTEMPT_COUNTS_KEY, { ...counts, [countKey]: attemptNumber });
    write(ATTEMPT_KEY, attempt);

    return attemptNumber;
  },
  setMethod(method: ConnectMethod) {
    const attempt = connectTracking.get();

    if (attempt) {
      write(ATTEMPT_KEY, { ...attempt, method });
    }
  },
  startOauth(pageType: number, method?: ConnectMethod): ConnectAttempt {
    const network = getConnectNetwork(pageType) ?? String(pageType);
    const current = connectTracking.get();
    const base: ConnectAttempt =
      current && current.network === network && !current.startedAt
        ? current
        : { network };
    const attempt: ConnectAttempt = {
      ...base,
      method: method ?? base.method,
      startedAt: Date.now(),
      hasReturned: false,
    };

    write(ATTEMPT_KEY, attempt);

    return attempt;
  },
  markReturned(): ConnectAttempt | null {
    const attempt = connectTracking.get();

    if (!attempt?.startedAt || attempt.hasReturned) {
      return null;
    }

    write(ATTEMPT_KEY, { ...attempt, hasReturned: true });

    return attempt;
  },
  setFailureReason(failureReason: string) {
    const attempt = connectTracking.get();

    if (attempt) {
      write(ATTEMPT_KEY, { ...attempt, failureReason });
    }
  },
  clear() {
    remove(ATTEMPT_KEY);
  },
};

export const trackConnectOauthReturned = (
  result: ConnectOauthResult,
  error?: ApiClientError | null,
) => {
  const attempt = connectTracking.markReturned();

  if (!attempt) {
    return;
  }

  HeapUtils.track(HeapUtils.events.CONNECT_OAUTH_RETURNED, {
    network: attempt.network,
    method: attempt.method ?? null,
    result,
    error_code: getConnectErrorCode(error),
    error_message: error ? error.userMessage || error.message || null : null,
    duration_sec: getConnectDurationSec(attempt),
  });
};
